import { useRef, useState } from 'react'
import * as React from 'react'
import gql from 'graphql-tag'
import styled from 'styled-components'
import SwiperCore, { Navigation, Pagination } from 'swiper'
import { Swiper, SwiperSlide, SwiperRef } from 'swiper/react'
import { NewsItemImageSlider_AttachmentFragment } from 'generated/graphql'
import useIsMobile from '@app.modules/hooks/useIsMobile'
import { mobileBreakPoint } from 'app.styled/media'
import NewsSliderArrowLeft from 'svgIcons/NewsSliderArrowLeft'
import NewsSliderArrowRight from 'svgIcons/NewsSliderArrowRight'
import NewsItemImageZoom from './NewsItemImageZoom'

gql`
  fragment NewsItemImageSlider_Attachment on Attachment {
    id
    url
  }
`

type TProps = {
  data: (NewsItemImageSlider_AttachmentFragment | null)[] | null[]

  onClick?: () => void
  type: 'feed' | 'modal' | 'card'
  className?: string
}

// todo : 슬라이드 개수, 슬라이드 버튼 기능 구현

const NewsItemImageSlider: React.FC<TProps> = ({
  data,
  type,
  onClick,
  className,
}) => {
  const isMobile = useIsMobile()
  const [sliderCount, setSliderCount] = useState(0)
  const sliderRef = useRef<SwiperRef>(null)

  const pagination = {
    clickable: true,
    renderBullet: (_, className) => {
      return `<span class=${className}></span>`
    },
  }

  const prevButtonRef = useRef<HTMLButtonElement>(null)
  const nextButtonRef = useRef<HTMLButtonElement>(null)

  const swiperInit = (swiper: SwiperCore) => {
    const navigation = swiper.params.navigation
    if (typeof navigation !== 'boolean' && navigation) {
      navigation.prevEl = prevButtonRef.current
      navigation.nextEl = nextButtonRef.current
    }
    swiper.navigation.init()
    swiper.navigation.update()
  }

  return (
    <StyledWrapper
      className={className}
      data-type={type}
      data-img={data.length > 1 ? 'multi' : ''}
    >
      {data.length > 1 && (
        <>
          <div className="count">
            {sliderCount + 1}/{data.length}
          </div>
          {type !== 'card' && (
            <>
              <button className="left-btn" ref={prevButtonRef}>
                <NewsSliderArrowLeft isShow={sliderCount !== 0} />
              </button>
              <button className="right-btn" ref={nextButtonRef}>
                <NewsSliderArrowRight
                  isShow={sliderCount !== data.length - 1}
                />
              </button>
            </>
          )}
        </>
      )}
      <Swiper
        ref={sliderRef}
        onSlideChange={(swiper) => setSliderCount(swiper.activeIndex)}
        touchRatio={isMobile ? 1 : 0}
        pagination={pagination}
        className="swiper-container"
        modules={[Pagination, Navigation]}
        onInit={swiperInit}
      >
        {data.map((image, idx) => (
          <SwiperSlide className="img-wrapper" key={idx} onClick={onClick}>
            {type === 'modal' ? (
              <NewsItemImageZoom
                imageUrl={image?.url}
                isMulti={data.length > 1}
              />
            ) : (
              <img className="img" src={image?.url} alt="기업채널 소식" />
            )}
          </SwiperSlide>
        ))}
      </Swiper>
    </StyledWrapper>
  )
}

export default NewsItemImageSlider

const StyledWrapper = styled.div`
  position: relative;
  overflow: hidden;
  cursor: pointer;

  .swiper-container {
    z-index: 0;
    overflow: visible;
  }

  .swiper-pagination {
    bottom: -25px;
    margin-left: 0;
  }

  .swiper-pagination-bullet,
  .swiper-pagination-bullet-active {
    width: 10px;
    height: 10px;
    opacity: 1;
    background-color: #dddddd;
    margin: 0px 10px;
  }

  .swiper-pagination-bullet-active {
    background-color: #5ea9ff;
  }
  .count {
    position: absolute;
    top: 24px;
    right: 20px;
    height: 40px;
    z-index: 1;
    padding: 0 15px;
    border-radius: 3px;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.43;
    letter-spacing: -0.56px;
    color: #fff;
    background-color: rgba(51, 51, 51, 0.55);
    display: flex;
    align-items: center;
  }

  .left-btn {
    left: 24px;
  }

  .right-btn {
    right: 24px;
  }
  .right-btn,
  .left-btn {
    cursor: pointer;
    border: none;
    background: none;
    padding: 0px;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
  }

  .img-wrapper {
    position: relative;
    width: 100%;
    background-color: #333;
    &:after {
      content: '';
      display: block;
      padding-bottom: 100%;
    }
    .img {
      position: absolute;
      width: 100%;
      height: auto;
      object-fit: cover;
      aspect-ratio: 1 / 1;
      object-position: center;
    }
  }
  &[data-type='card'] {
    .img-wrapper {
      position: relative;
      background-color: #333;
      cursor: default;
      &:after {
        display: none;
      }
      .img {
        position: relative;
        height: 255px;
      }
    }
  }
  &[data-type='modal'] {
    .img-wrapper {
      cursor: default;
    }
  }

  &[data-img='multi'] {
    padding-bottom: 30px;
    &[data-type='card'] {
      padding-bottom: 0;
    }
  }

  ${mobileBreakPoint} {
    &[data-type='card'] {
      .img-wrapper {
        .img {
          height: 200px;
        }
      }
    }
  }
`
